import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';
import { showToast } from '../toast';
import { store } from '../state';
import type { Ticker } from '../state';

// Defense Monitor — surfaces the validated defensive battery on the actual book.
// "Am I positioned to survive a bad regime, and sized to not get carried out?"
interface DefHolding {
  sym: string; weight: number; dist: number | null; bull: boolean;
  ladder: number; expo: number; annVol: number | null; volZ: number | null;
  ddFromHi: number; er: number | null;
  topRisk: string; topScore: number; topSell: string; topFlags: string[];
}
interface DefData {
  posture: 'GREEN' | 'YELLOW' | 'RED';
  reasons: string[];
  portfolio: {
    effN: number; nHoldings: number; belowFrac: number; recCashFrac: number;
    avgCorr: number; stressCorr: number; maxDD: number; ddBand1: number; ddBand2: number;
    toppingFrac: number; distributionFrac: number; toppingNaFrac: number; toppingNames: string[];
  };
  holdings: DefHolding[];
  computedAt: string;
}

const TOP_COL: Record<string, string> = {
  distribution: 'var(--red)', watch: 'var(--warn)', none: 'var(--green)', 'n/a': 'var(--text3)',
};

const POSTURE_COL: Record<string, string> = { GREEN: 'var(--green)', YELLOW: 'var(--warn)', RED: 'var(--red)' };
const POSTURE_MSG: Record<string, string> = {
  GREEN: 'Defensively sound', YELLOW: 'Trim / watch', RED: 'De-risk now',
};

function pct(x: number | null, signed = false): string {
  if (x == null) return '—';
  const v = (x * 100);
  return (signed && v >= 0 ? '+' : '') + v.toFixed(1) + '%';
}

export class DefenseTab implements Tab {
  readonly id = 'defense';
  private currentTickers: string[] = [];
  private currentShares: string[] = [];

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.el('def-analyze-btn').addEventListener('click', () => this.analyzeInput());
    this.el('def-portfolio-btn').addEventListener('click', () => this.loadPortfolio());
    this.el('def-refresh-btn').addEventListener('click', () => { if (this.currentTickers.length) this.load(this.currentTickers, this.currentShares); });
    (this.el('def-ticker-input') as HTMLInputElement)
      .addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') this.analyzeInput(); });
  }

  render(): void {
    if (!this.currentTickers.length) this.loadPortfolio();
  }

  private analyzeInput(): void {
    const raw = (this.el('def-ticker-input') as HTMLInputElement).value.trim().toUpperCase();
    if (!raw) return;
    const tickers = raw.split(',').map(t => t.trim()).filter(Boolean).slice(0, 12);
    this.load(tickers, tickers.map(() => ''));   // manual entry -> equal weight
  }

  private loadPortfolio(): void {
    const rows = (store.get('tickers') as Ticker[]).filter(h => (h.Ticker ?? '').trim());
    const tickers = rows.map(h => (h.Ticker ?? '').toUpperCase().trim()).slice(0, 12);
    const shares = rows.map(h => (h.Shares ?? '')).slice(0, 12);
    if (!tickers.length) {
      showToast('No portfolio tickers — sync Holdings or enter tickers manually', 'warn');
      return;
    }
    (this.el('def-ticker-input') as HTMLInputElement).value = tickers.join(', ');
    this.load(tickers, shares);
  }

  private async load(tickers: string[], shares: string[]): Promise<void> {
    this.currentTickers = tickers;
    this.currentShares = shares;
    this.el('def-body').innerHTML = `
      <div style="padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">
        Reading defensive posture on ${tickers.length} holding${tickers.length > 1 ? 's' : ''}…
        regime · volatility · concentration · correlation · drawdown
      </div>`;
    try {
      const data = await workerFetch<DefData>('/api/defense', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers, shares }),
        signal: AbortSignal.timeout(60000),
      });
      if ((data as any).error) throw new Error((data as any).error);
      this.el('def-updated').textContent = `Read ${timeAgo(data.computedAt)}`;
      this.renderRead(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.el('def-body').innerHTML =
        `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderRead(d: DefData): void {
    const col = POSTURE_COL[d.posture];
    const p = d.portfolio;

    const banner = `
      <div style="display:flex;align-items:center;gap:18px;background:var(--panel);border:1px solid var(--border);
                  border-left:4px solid ${col};border-radius:5px;padding:16px 20px;margin-bottom:16px;">
        <div style="font-family:var(--mono);font-size:26px;font-weight:700;color:${col};letter-spacing:0.08em;">${d.posture}</div>
        <div style="min-width:0;">
          <div style="font-size:13px;font-weight:700;color:${col};">${POSTURE_MSG[d.posture]}</div>
          <div style="font-size:11px;color:var(--text3);margin-top:3px;">
            ${d.reasons.length ? d.reasons.map(r => esc(r)).join(' · ') : 'regime risk-on, diversified, vol normal, sized to survive'}
          </div>
        </div>
      </div>`;

    const stat = (label: string, val: string, sub: string, c = 'var(--text)') => `
      <div style="background:var(--panel);border:1px solid var(--border);border-radius:4px;padding:12px 14px;">
        <div style="font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">${label}</div>
        <div style="font-family:var(--mono);font-size:18px;font-weight:700;color:${c};margin-top:4px;">${val}</div>
        <div style="font-size:10px;color:var(--text3);margin-top:2px;">${sub}</div>
      </div>`;

    const cashCol = p.recCashFrac >= 0.4 ? 'var(--red)' : p.recCashFrac >= 0.15 ? 'var(--warn)' : 'var(--green)';
    const corrCol = p.avgCorr >= 0.75 ? 'var(--warn)' : 'var(--text)';
    const topCol = p.distributionFrac >= 0.4 ? 'var(--red)' : (p.toppingFrac >= 0.4 || p.distributionFrac > 0) ? 'var(--warn)' : 'var(--green)';
    const topSub = Math.round(p.distributionFrac * 100) + '% active distribution'
      + (p.toppingNaFrac > 0 ? ' · ' + Math.round(p.toppingNaFrac * 100) + '% n/a' : '');
    const summary = `
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:10px;margin-bottom:18px;">
        ${stat('Concentration', p.effN.toFixed(1) + ' / ' + p.nHoldings, 'effective holdings (1/HHI)')}
        ${stat('Regime de-risk', pct(p.recCashFrac), Math.round(p.belowFrac * 100) + '% below 200d → hold cash', cashCol)}
        ${stat('Diversification', p.avgCorr.toFixed(2), 'avg corr now · ' + p.stressCorr.toFixed(2) + ' in stress', corrCol)}
        ${stat('Drawdown risk', pct(p.maxDD), '16%: >' + pct(p.ddBand1).replace('%', '') + '% · 2.5%: >' + pct(p.ddBand2).replace('%', '') + '% ahead', 'var(--warn)')}
        ${stat('Topping risk', pct(p.toppingFrac), topSub + ' (Wyckoff)', topCol)}
      </div>`;

    const rows = d.holdings.map(h => {
      const regCol = h.bull ? 'var(--green)' : 'var(--red)';
      const expoCol = h.expo >= 0.99 ? 'var(--green)' : h.expo <= 0.01 ? 'var(--red)' : 'var(--warn)';
      const vzCol = (h.volZ != null && h.volZ > 1.0) ? 'var(--warn)' : 'var(--text)';
      const ddCol = h.ddFromHi <= -0.2 ? 'var(--red)' : h.ddFromHi <= -0.1 ? 'var(--warn)' : 'var(--text)';
      const topCol = TOP_COL[h.topRisk] || 'var(--text3)';
      const topTxt = h.topRisk === 'n/a' ? 'n/a' : h.topRisk + (h.topSell ? ' ' + h.topSell : '');
      const topTitle = h.topFlags && h.topFlags.length ? esc(h.topFlags.join(', ')) : '';
      return `
        <tr style="border-bottom:1px solid var(--border);">
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;font-weight:700;color:var(--accent);">${esc(h.sym)}</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;">${pct(h.weight)}</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;color:${regCol};">${pct(h.dist, true)}</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:11px;font-weight:700;color:${regCol};">${h.bull ? 'BULL' : 'BEAR'}</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;color:${expoCol};">${Math.round(h.expo * 100)}%</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;">${h.annVol != null ? Math.round(h.annVol * 100) + '%' : '—'}</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;color:${vzCol};">${h.volZ != null ? (h.volZ >= 0 ? '+' : '') + h.volZ.toFixed(1) : '—'}</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;color:${ddCol};">${pct(h.ddFromHi, true)}</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;color:var(--text3);">${h.er != null ? h.er.toFixed(2) : '—'}</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;font-weight:700;color:${topCol};" title="${topTitle}">${topTxt}</td>
        </tr>`;
    }).join('');

    const th = (t: string) => `<th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">${t}</th>`;
    const table = `
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr style="border-bottom:2px solid var(--border);">
          ${th('Sym')}${th('Wt')}${th('vs 200d')}${th('Regime')}${th('Exposure')}${th('Ann Vol')}${th('Vol z')}${th('From Hi')}${th('Trend ER')}${th('Topping')}
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="font-size:10px;color:var(--text3);margin-top:12px;font-style:italic;">
        Exposure = 200d gate × 9/20/30 EMA ladder (below 200d → cash). Topping = Wyckoff distribution read
        (none/watch/distribution; hover for events) — descriptive context, needs volume (crypto → n/a).
        A read, not advice — the validated defensive battery on your book.
      </div>`;

    this.el('def-body').innerHTML = banner + summary + table;
  }
}
